import React, { useRef } from 'react';
import { Handle, Position } from 'reactflow';
import { PersonaAvatar } from '../../../../personas/PersonaAvatar';
import { ExecutionSection } from './ExecutionSection';
import { useTheme } from '../../../../../contexts/ThemeContext';
import { useNodeDimensions } from '../../hooks/useNodeDimensions';
import { useTabFocalTrigger } from '../../hooks/useTabFocalTrigger';
import { turnMarkerAriaLabel } from '../ariaLabels';
import type { TurnMarkerData } from '../../types';

// v0.4 Task 6: rich turn marker.
//
//   role=user     → prompt card (bold header + full prompt, clamped
//                   to ~8 lines). No telemetry footer.
//   role=asst     → PersonaCardBody (primary persona threaded via
//                   data.primaryPersona) + ExecutionSection.
//
// wrapperRef lives here so Task 10 can wire useNodeDimensions.

export interface TurnMarkerNodeRichProps {
  id: string;
  data: TurnMarkerData;
}

export const TurnMarkerNodeRich: React.FC<TurnMarkerNodeRichProps> = ({
  id,
  data,
}) => {
  const { darkMode } = useTheme();
  const wrapperRef = useRef<HTMLDivElement>(null);
  useNodeDimensions(id, wrapperRef);
  const tabTrigger = useTabFocalTrigger(data.turnKey);
  const ariaLabel = turnMarkerAriaLabel(data);
  const isUser = data.role === 'user';
  const execId = `exec-${id}`;

  if (isUser) {
    return (
      <div
        ref={wrapperRef}
        role="button"
        tabIndex={0}
        aria-label={ariaLabel}
        onFocus={tabTrigger.onFocus}
        onBlur={tabTrigger.onBlur}
        className={`w-[360px] rounded-lg shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 focus-visible:ring-offset-2 ${
          darkMode
            ? 'bg-sky-900/30 border border-sky-700'
            : 'bg-sky-50 border border-sky-200'
        }`}
        data-node-id={id}
      >
        <Handle
          type="target"
          position={Position.Top}
          id="t"
          className="!opacity-0 !pointer-events-none"
        />
        <Handle
          type="source"
          position={Position.Bottom}
          id="b"
          className="!opacity-0 !pointer-events-none"
        />
        <div
          className={`p-4 border-b ${
            darkMode ? 'border-sky-800' : 'border-sky-200'
          }`}
        >
          <div
            className={`font-bold ${
              darkMode ? 'text-sky-200' : 'text-sky-900'
            }`}
          >
            👤 You
          </div>
          {data.timestamp && (
            <div
              className={`text-[10px] font-mono mt-0.5 ${
                darkMode ? 'text-sky-400' : 'text-sky-600'
              }`}
            >
              {new Date(data.timestamp).toLocaleString()}
            </div>
          )}
        </div>
        <div className="p-4">
          <div
            className={`whitespace-pre-wrap break-words text-sm ${
              darkMode ? 'text-gray-200' : 'text-gray-800'
            }`}
            style={{
              display: '-webkit-box',
              WebkitLineClamp: 4,
              WebkitBoxOrient: 'vertical',
              overflow: 'hidden',
            }}
          >
            {data.fullContent ?? data.preview ?? ''}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      ref={wrapperRef}
      role="button"
      tabIndex={0}
      aria-label={ariaLabel}
      aria-describedby={execId}
      onFocus={tabTrigger.onFocus}
      onBlur={tabTrigger.onBlur}
      className={`w-[360px] rounded-lg shadow-md focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500/60 focus-visible:ring-offset-2 ${
        darkMode
          ? 'bg-gray-800 border border-gray-700'
          : 'bg-white border border-gray-200'
      }`}
      data-node-id={id}
    >
      <Handle
        type="target"
        position={Position.Top}
        id="t"
        className="!opacity-0 !pointer-events-none"
      />
      <Handle
        type="source"
        position={Position.Bottom}
        id="b"
        className="!opacity-0 !pointer-events-none"
      />
      {/* Compact persona header — full PersonaCardBody would duplicate
          the page-level persona card on every asst turn. Keep just
          avatar + name + specialization as an agent signature. */}
      {(() => {
        const p = data.primaryPersona ?? null;
        const name: string = p?.name ?? 'Assistant';
        const specialization: string =
          p?.primarySpecialization ??
          (p?.tags && p.tags.length > 0 ? p.tags[0] : '🤖 assistant');
        return (
          <div
            className={`p-3 flex items-center gap-3 border-b ${
              darkMode ? 'bg-gray-700 border-gray-600' : 'bg-gray-50 border-gray-200'
            }`}
          >
            {p ? (
              <PersonaAvatar
                name={name}
                specialization={specialization}
                avatarUrl={p.avatarUrl}
                size="small"
              />
            ) : (
              <div
                className={`w-8 h-8 rounded-full flex items-center justify-center text-xs ${
                  darkMode
                    ? 'bg-gray-600 text-gray-300'
                    : 'bg-gray-200 text-gray-700'
                }`}
              >
                🤖
              </div>
            )}
            <div className="min-w-0 flex-1">
              <div
                className={`font-bold text-sm truncate ${
                  darkMode ? 'text-white' : 'text-gray-900'
                }`}
              >
                {name}
              </div>
              <div
                className={`text-[11px] truncate ${
                  darkMode ? 'text-gray-400' : 'text-gray-500'
                }`}
              >
                {specialization}
              </div>
            </div>
          </div>
        );
      })()}
      {/* Response preview omitted — chat bubble has the full answer.
          Keep the exec footer lean: model + tokens + cost. */}
      <ExecutionSection
        model={data.modelUsed}
        totalTokens={data.totalTokens}
        costUsd={data.totalCostUsd}
        execSectionId={execId}
      />
    </div>
  );
};
